/**
 * custom-contact-form-app.js
 *
 * お問い合わせフォーム (Shopify Forms app inline block) の DOM 後加工:
 *
 *   ログイン顧客のメール自動補完 + 編集ロック — `customer.email` が
 *   存在する場合のみ、Forms app の email input を find して値を埋め、
 *   readOnly + 視覚スタイルを付与する。
 *
 * Forms app はクライアント側でフォームを後から描画するため、以下 3 段階で
 * email input の検出を試みる:
 *
 *   1. 初期 DOM スキャン (script 実行時に既に描画済みなら即時 apply)
 *   2. MutationObserver で section 配下の DOM 変化を監視
 *      (subtree=true で深い変更もキャッチ。Shadow DOM もカバー)
 *   3. ポーリング fallback (Forms app が closed Shadow DOM or iframe で
 *      MutationObserver が届かないケースに備えて、500ms 間隔で 30 秒間 retry)
 *
 * Shadow DOM 対応: querySelectorDeep / observeShadowRoots で open shadow root を
 * 再帰的に探索。closed shadow root は JS から触れないが、ポーリング fallback で
 * Forms app の onload を待つ。
 *
 * Theme editor 対応: `shopify:section:load` イベントで新規ロード section も
 * 初期化。二重実行は per-section data attribute guard で防止。
 *
 * 値は各 section の data-* 属性から取得 (Liquid で escape された値):
 *   - data-customer-email: 顧客のメールアドレス (空文字なら prefill しない)
 *   - data-readonly-tooltip: readonly 化された input の tooltip 文言 (i18n)
 *
 * 規約パネル (`.contact-form-terms`) は Liquid 側で form の **下部** に
 * 静的配置済み (顧客判断 2026-05-13)。本 JS では DOM 移動を行わない。
 *
 * Shadow DOM 内幅広げ (顧客判断 2026-05-14):
 *   Forms app の `<shopify-forms-embed>` は open shadow root を持ち、
 *   内部の `#form-container-ref` / `[data-shadow-host]` に max-width や
 *   padding-inline が固定で入っているため、SP / タブレットでフォームが
 *   極端に狭く見える。Shadow root に `<style>` 要素を注入してこれらを
 *   100% 化する。
 */
(() => {
  'use strict';

  // Forms app の email 入力欄を find するセレクタ群。Forms app は
  // `type="text"` + `inputmode="email"` + `data-testid="field-email"` という
  // 非標準パターンで描画するため、複数の経路で検出する。
  // `:not([type="hidden"])` で CSRF トークン等の隠しフィールド誤検出を防止。
  //
  // 配列順に querySelector を試行するため、優先度の高いセレクタ (より厳密に
  // 一致するもの) を上に並べる。カンマ結合した複合セレクタだと DOM 出現順で
  // マッチするため、`email_confirmation` のような部分一致フィールドがメインの
  // `type="email"` より前に出現するケースで誤検出する。
  const EMAIL_SELECTORS = [
    'input[type="email"]',
    'input[data-testid="field-email"]:not([type="hidden"])',
    'input[id="email" i]:not([type="hidden"])',
    'input[inputmode="email" i]:not([type="hidden"])',
    'input[name="email" i]:not([type="hidden"])',
    'input[autocomplete="email" i]:not([type="hidden"])',
    'input[name*="email" i]:not([type="hidden"])',
    'input[autocomplete*="email" i]:not([type="hidden"])',
  ];

  /**
   * root 配下を再帰的に querySelector する (open shadow root 含む)。
   * Forms app が Web Component (shadow DOM) で描画するケースを救う。
   */
  const querySelectorDeep = (root, selector) => {
    const direct = root.querySelector?.(selector);
    if (direct) return direct;

    const all = root.querySelectorAll?.('*') || [];
    for (const el of all) {
      if (el.shadowRoot) {
        const inShadow = querySelectorDeep(el.shadowRoot, selector);
        if (inShadow) return inShadow;
      }
    }
    return null;
  };

  const findEmailInput = (root) => {
    for (const selector of EMAIL_SELECTORS) {
      const input = querySelectorDeep(root, selector);
      if (input) return input;
    }
    return null;
  };

  /**
   * Forms app の open shadow root に幅広げ用 CSS を注入する。
   * 既に注入済みなら no-op (data 属性で guard)。
   * 戻り値: 1 つ以上の shadow root に注入できた場合 true。
   *
   * 注入 CSS の意図 (実 DOM 構造ベース 2026-05-14):
   * - 安定セレクタで Forms app の form 領域全体を 100% 幅化:
   *   - #app-embed / [data-form-root] / #form-container-ref /
   *     [data-sizing="form-wrapper"] / [data-testid="app-router"]
   * - 内部の hashed class (`_formContainer_*`, `_grid_*`, `_inline_*`,
   *   `_container_*`) は ビルド毎に変動するため、影響範囲を絞った上で
   *   `[class*="..."]` 部分一致で robust にマッチ
   * - FORMS_APP_GRID_BREAKPOINT_PX 以下で `display: grid` を解除し、
   *   Forms app の内部 grid 計算による幅制約を無効化
   *
   * FORMS_APP_GRID_BREAKPOINT_PX (= 490) は Forms app 内部 CSS の
   * `@media (max-width: 490px)` が grid 切替を行っている境界値で、
   * これは foreign value (外部依存) であり、テーマ側のデザイントークンとは
   * 独立した値。Forms app 側で値が変わった場合は本定数も追従更新が必要。
   *
   * なお host 要素 (`<shopify-forms-embed>`) の CSS Custom Property
   * (`--container-max-w` / `--inline-container-max-width` /
   * `--app-embed-padding-x`) は CSS ファイル側で上書きするので、本 JS では
   * インナーの leaf 要素のみを直接ターゲットする。
   */
  const SHADOW_STYLE_TAG = 'libertaContactFormWidth';
  const FORMS_APP_GRID_BREAKPOINT_PX = 490;
  const SHADOW_OVERRIDE_CSS = `
    #app-embed,
    [data-form-root],
    [data-testid="app-router"],
    #form-container-ref,
    [data-sizing="form-wrapper"] {
      width: 100% !important;
      max-width: 100% !important;
    }
    [data-form-root] [class*="_formContainer_"],
    [data-form-root] [class*="_inline_"],
    [data-form-root] [class*="_gridItem_"] {
      width: 100% !important;
      max-width: 100% !important;
    }
    @media screen and (max-width: ${FORMS_APP_GRID_BREAKPOINT_PX}px) {
      [data-form-root][class*="_container_"],
      [data-form-root] [class*="_container_"],
      [data-form-root][class*="_grid_"],
      [data-form-root] [class*="_grid_"] {
        display: block !important;
      }
    }
  `;

  const injectShadowOverride = (root) => {
    let injected = false;
    const visit = (node) => {
      const all = node.querySelectorAll?.('*') || [];
      for (const el of all) {
        if (el.shadowRoot && !el.shadowRoot.querySelector(`style[data-injected="${SHADOW_STYLE_TAG}"]`)) {
          const style = document.createElement('style');
          style.setAttribute('data-injected', SHADOW_STYLE_TAG);
          style.textContent = SHADOW_OVERRIDE_CSS;
          el.shadowRoot.appendChild(style);
          injected = true;
        }
        if (el.shadowRoot) visit(el.shadowRoot);
      }
    };
    visit(root);
    return injected;
  };

  /**
   * フォーム送信完了時に規約パネルを非表示にする (顧客判断 2026-05-15)。
   *
   * Forms app は送信成功後に `[data-form-root]` の `data-current-step` 属性を
   * `"form"` → `"success"` / `"error"` / `"thankYou"` 等に切り替える。これを
   * 検出して `.contact-form-terms` を hidden 化する。
   *
   * 判定方針: `data-current-step` を明示的なホワイトリスト
   * `FORM_STEP_DONE_VALUES` で照合し、完了系の値のときのみ hidden 化する。
   * 空文字や `'form'` / `'loading'` 等の中間ステートでは何もせず、
   * バリデーション中などで誤って規約を消さないようにする。
   *
   * 戻り値: ポーリング停止条件を満たした場合 true。すなわち:
   *   - 規約パネル要素が存在しない (Liquid 側で配置されていない / 削除済み)
   *   - 規約パネルを非表示にした (= 送信完了系のステップに遷移)
   * いずれにも該当しない (まだ入力中 / 未描画) 場合は false (= ポーリング継続)。
   */
  const FORM_STEP_DONE_VALUES = ['success', 'error', 'thankYou'];
  const syncTermsVisibility = (section) => {
    const terms = section.querySelector('.contact-form-terms');
    // 規約パネル自体が DOM にない場合は監視する意味がないので終了 (true 返却)。
    if (!terms) return true;
    // Forms app embed は安定して `<shopify-forms-embed>` host にぶら下がる。
    // host.shadowRoot 経由で formRoot を直接取得すれば querySelectorAll('*') の
    // 再帰探索 (querySelectorDeep) を避けられる。
    // (Forms app が複数の入れ子 shadow root を作るレアケースだけ fallback)。
    const embed = section.querySelector('shopify-forms-embed');
    const formRoot =
      embed?.shadowRoot?.querySelector('[data-form-root]')
      || querySelectorDeep(section, '[data-form-root]');
    if (!formRoot) return false;
    const step = formRoot.dataset.currentStep;
    if (FORM_STEP_DONE_VALUES.includes(step)) {
      terms.hidden = true;
      return true;
    }
    return false;
  };

  const applyEmailPrefill = (root, email, tooltip) => {
    const input = findEmailInput(root);
    if (!input) return false;
    // 既にユーザーが手入力した値があれば上書きしない
    if (input.value && input.value !== email) return true;
    if (input.dataset.readonlyPrefill === 'true') return true;

    // React/Vue 等の framework が input.value を内部 state で管理しているケース
    // に備えて、native setter で直接書き込む (React の synthetic event を発火させる
    // 標準的なパターン)
    const nativeSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    )?.set;
    if (nativeSetter) {
      nativeSetter.call(input, email);
    } else {
      input.value = email;
    }

    input.readOnly = true;
    input.setAttribute('aria-readonly', 'true');
    input.dataset.readonlyPrefill = 'true';
    if (tooltip) input.setAttribute('title', tooltip);

    // Forms app 側のバリデーション / state を同期
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  };

  const initSection = (section) => {
    if (section.dataset.contactFormAppInitialized === 'true') return;
    section.dataset.contactFormAppInitialized = 'true';

    const email = section.getAttribute('data-customer-email') || '';
    const tooltip = section.getAttribute('data-readonly-tooltip') || '';

    // 幅広げ CSS は未ログインユーザにも適用したいので、email 有無に関係なく走らせる。
    // email prefill は customer.email がある場合のみ。
    let emailDone = !email;
    let shadowDone = false;

    const tryApply = () => {
      if (injectShadowOverride(section)) {
        // shadow root が見つかれば注入は成功。Forms app 内 reflow を即座に発火させる。
        shadowDone = true;
      }
      if (!emailDone && applyEmailPrefill(section, email, tooltip)) {
        emailDone = true;
      }
      // 規約パネルの表示制御 (form ステップ以外なら hidden) を毎回同期。
      // 初回描画と送信後の状態変化の両方を救うため、tryApply 完了判定とは
      // 独立して毎回呼ぶ。
      syncTermsVisibility(section);
      return shadowDone && emailDone;
    };

    // 1. 規約パネル表示制御のポーリングを先に開始 (送信後の data-current-step
    //    変化はいつでも起こりうるため、shadow 注入 / email prefill の完了とは
    //    独立して走らせる)。
    //    停止条件:
    //      - section が DOM から外れた (テーマエディタの再描画等) → メモリリーク防止
    //      - syncTermsVisibility が true (= 規約 hidden 化) を返した = 送信完了確定
    //        (お問い合わせの success/thankYou から「送信前に戻る」フローは想定外
    //        のため終了 OK)
    //    MutationObserver は open shadow DOM 内の attribute 変化を観測できない
    //    ケースがあるためポーリング採用。1 秒間隔で軽量チェック。
    const termsPollId = setInterval(() => {
      if (!section.isConnected || syncTermsVisibility(section)) {
        clearInterval(termsPollId);
      }
    }, 1000);

    // 2. 初期スキャン (既に描画済みなら即時、tryApply で shadow 注入 + email
    //    prefill + 規約表示同期がすべて完了)。完了している場合は以降の
    //    MutationObserver / 500ms pollId / timeout は不要なので早期 return
    //    (termsPollId は引き続き稼働し続ける)。
    if (tryApply()) return;

    // 3. MutationObserver で DOM 変化を監視 (初期 prefill / shadow 注入用)
    const observer = new MutationObserver(() => {
      if (tryApply()) {
        observer.disconnect();
        clearInterval(pollId);
        clearTimeout(timeoutId);
      }
    });
    observer.observe(section, { childList: true, subtree: true });

    // 4. ポーリング fallback (Forms app が closed shadow DOM or iframe で
    //    MutationObserver が届かないケースを救う)
    const pollId = setInterval(() => {
      if (tryApply()) {
        clearInterval(pollId);
        observer.disconnect();
        clearTimeout(timeoutId);
      }
    }, 500);

    // セーフティ: 30 秒経っても完了しない場合は監視終了。
    const timeoutId = setTimeout(() => {
      observer.disconnect();
      clearInterval(pollId);
    }, 30000);
  };

  const initAll = (root = document) => {
    root.querySelectorAll('[data-contact-form-section]').forEach(initSection);
  };

  initAll();

  // Theme editor: セクション再描画時に新規セクションも初期化
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) initAll(event.target);
  });
})();
